/**
 * Utility functions to persist and restore navigation state across page reloads
 */

const NAVIGATION_STATE_KEY = 'cellreveal_nav_state';

export type NavigationState = Record<string, unknown>;

/**
 * Save navigation state to sessionStorage
 */
export function saveNavigationState<T>(key: string, state: T): void {
  try {
    const existing = sessionStorage.getItem(NAVIGATION_STATE_KEY);
    const allStates = existing ? JSON.parse(existing) : {};
    allStates[key] = state;
    sessionStorage.setItem(NAVIGATION_STATE_KEY, JSON.stringify(allStates));
  } catch (error) {
    console.error('Failed to save navigation state:', error);
  }
}

/**
 * Restore navigation state from sessionStorage
 */
export function restoreNavigationState<T = NavigationState>(key: string): T | null {
  try {
    const existing = sessionStorage.getItem(NAVIGATION_STATE_KEY);
    if (!existing) return null;
    const allStates = JSON.parse(existing);
    return allStates[key] || null;
  } catch (error) {
    console.error('Failed to restore navigation state:', error);
    return null;
  }
}

/**
 * Clear a specific navigation state
 */
export function clearNavigationState(key: string): void {
  try {
    const existing = sessionStorage.getItem(NAVIGATION_STATE_KEY);
    if (!existing) return;
    const allStates = JSON.parse(existing);
    delete allStates[key];
    sessionStorage.setItem(NAVIGATION_STATE_KEY, JSON.stringify(allStates));
  } catch (error) {
    console.error('Failed to clear navigation state:', error);
  }
}

/**
 * Clear all navigation states
 */
export function clearAllNavigationStates(): void {
  try {
    sessionStorage.removeItem(NAVIGATION_STATE_KEY);
  } catch (error) {
    console.error('Failed to clear all navigation states:', error);
  }
}
